/**
 * 🔍 Barcodes aus einem Foto lesen (für den Barcode-Stapel, Sonderfunktion „batchBarcodes").
 * 1) Native BarcodeDetector (Android-WebView/Chrome): schnell, findet ALLE Codes im Bild.
 * 2) Fallback zxing (JS): liest je Durchgang einen Code — auf Voll- und Halbbild versucht.
 * Rückgabe immer ein Array [{text, format}] — nie eine Exception (Erfassen darf nie hängen).
 */
import { BrowserMultiFormatReader } from '@zxing/browser';
import { DecodeHintType, BarcodeFormat } from '@zxing/library';

const ZX_FORMATE = {
  0: 'aztec', 1: 'codabar', 2: 'code_39', 3: 'code_93', 4: 'code_128', 5: 'data_matrix', 6: 'ean_8',
  7: 'ean_13', 8: 'itf', 9: 'maxicode', 10: 'pdf417', 11: 'qr_code', 12: 'rss_14', 13: 'rss_expanded',
  14: 'upc_a', 15: 'upc_e', 16: 'upc_ean_extension',
};

function bildLaden(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

function canvasAus(img, maxSeite) {
  const r = Math.min(1, maxSeite / Math.max(img.naturalWidth, img.naturalHeight));
  const c = document.createElement('canvas');
  c.width = Math.round(img.naturalWidth * r);
  c.height = Math.round(img.naturalHeight * r);
  c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
  return c;
}

async function nativLesen(img) {
  if (!window.BarcodeDetector) return null;
  try {
    const formate = window.BarcodeDetector.getSupportedFormats ? await window.BarcodeDetector.getSupportedFormats() : undefined;
    const det = new window.BarcodeDetector(formate && formate.length ? { formats: formate } : undefined);
    const res = await det.detect(canvasAus(img, 3000));
    return (res || []).map((r) => ({ text: r.rawValue, format: r.format }));
  } catch { return null; }
}

async function zxingLesen(img) {
  const hints = new Map();
  hints.set(DecodeHintType.TRY_HARDER, true);
  // Nur Formate, die der Stapel wieder darstellen kann — sonst liefert zxing evtl. z. B. den
  // Aztec-Code eines DPD-Labels statt des Code 128 (liest nur EINEN Code pro Durchgang).
  hints.set(DecodeHintType.POSSIBLE_FORMATS, [
    BarcodeFormat.CODE_128, BarcodeFormat.CODE_39, BarcodeFormat.CODE_93, BarcodeFormat.ITF,
    BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A,
    BarcodeFormat.DATA_MATRIX, BarcodeFormat.QR_CODE,
  ]);
  const reader = new BrowserMultiFormatReader(hints);
  const out = [];
  for (const seite of [2200, 1400]) {
    try {
      const r = await reader.decodeFromCanvas(canvasAus(img, seite));
      if (r && r.getText()) out.push({ text: r.getText(), format: ZX_FORMATE[r.getBarcodeFormat()] || String(r.getBarcodeFormat()) });
    } catch { /* kein Code in dieser Größe */ }
  }
  return out;
}

export async function barcodesAusFoto(file) {
  let geladen;
  try { geladen = await bildLaden(file); } catch { return []; }
  try {
    const nativ = await nativLesen(geladen.img);
    if (nativ && nativ.length) return nativ;
    return await zxingLesen(geladen.img);
  } catch {
    return [];
  } finally {
    try { URL.revokeObjectURL(geladen.url); } catch { /* */ }
  }
}
